import {
  ModpackNote,
  PaginatedModpackNotesResponse,
  ModpackNoteSearchResponse,
  ClusterAllNotesResponse,
  CreateModpackNoteRequest,
  UpdateModpackNoteRequest,
} from '../types/modpackNote';

const MODPACK_NOTES_API_BASE_URL = 'https://spy.playcdu.co/api/notes/modpack-notes';

class ModpackNotesApiService {
  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const response = await fetch(`${MODPACK_NOTES_API_BASE_URL}${endpoint}`, {
      ...options,
      // Cloudflare Access handles authentication via cookies
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API Error: ${response.status} ${response.statusText} - ${errorText}`);
    }

    return response.json();
  }

  async getAllNotes(page?: number, limit?: number): Promise<PaginatedModpackNotesResponse> {
    const params = new URLSearchParams();
    if (page !== undefined) params.append('page', page.toString());
    if (limit !== undefined) params.append('limit', limit.toString());
    const qs = params.toString();
    return this.request<PaginatedModpackNotesResponse>(qs ? `?${qs}` : '');
  }

  async searchNotes(query: string, page?: number, limit?: number): Promise<ModpackNoteSearchResponse> {
    const params = new URLSearchParams({ q: query });
    if (page !== undefined) params.append('page', page.toString());
    if (limit !== undefined) params.append('limit', limit.toString());
    return this.request<ModpackNoteSearchResponse>(`/search?${params.toString()}`);
  }

  async getNoteById(id: string): Promise<ModpackNote> {
    return this.request<ModpackNote>(`/${encodeURIComponent(id)}`);
  }

  async getNotesByFqdn(fqdn: string, page?: number, limit?: number): Promise<PaginatedModpackNotesResponse> {
    const params = new URLSearchParams();
    if (page !== undefined) params.append('page', page.toString());
    if (limit !== undefined) params.append('limit', limit.toString());
    const qs = params.toString();
    return this.request<PaginatedModpackNotesResponse>(`/fqdn/${encodeURIComponent(fqdn)}${qs ? `?${qs}` : ''}`);
  }

  async getNotesByCluster(clusterId: string, page?: number, limit?: number): Promise<PaginatedModpackNotesResponse> {
    const params = new URLSearchParams();
    if (page !== undefined) params.append('page', page.toString());
    if (limit !== undefined) params.append('limit', limit.toString());
    const qs = params.toString();
    return this.request<PaginatedModpackNotesResponse>(`/cluster/${encodeURIComponent(clusterId)}${qs ? `?${qs}` : ''}`);
  }

  async getAllClusterNotes(
    clusterId: string,
    fqdns: string[],
    page?: number,
    limit?: number
  ): Promise<ClusterAllNotesResponse> {
    const params = new URLSearchParams();
    if (fqdns.length > 0) params.append('fqdns', fqdns.join(','));
    if (page !== undefined) params.append('page', page.toString());
    if (limit !== undefined) params.append('limit', limit.toString());
    const qs = params.toString();
    return this.request<ClusterAllNotesResponse>(`/cluster/${encodeURIComponent(clusterId)}/all${qs ? `?${qs}` : ''}`);
  }

  async getNotesByCreator(creatorId: string, page?: number, limit?: number): Promise<PaginatedModpackNotesResponse> {
    const params = new URLSearchParams();
    if (page !== undefined) params.append('page', page.toString());
    if (limit !== undefined) params.append('limit', limit.toString());
    const qs = params.toString();
    return this.request<PaginatedModpackNotesResponse>(`/creator/${encodeURIComponent(creatorId)}${qs ? `?${qs}` : ''}`);
  }

  async createNote(data: CreateModpackNoteRequest): Promise<{ ok: boolean; id: string }> {
    return this.request<{ ok: boolean; id: string }>('', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  async updateNote(id: string, data: UpdateModpackNoteRequest): Promise<ModpackNote> {
    return this.request<ModpackNote>(`/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  async deleteNote(id: string): Promise<{ ok: boolean; deleted: string }> {
    return this.request<{ ok: boolean; deleted: string }>(`/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }
}

export const modpackNotesApiService = new ModpackNotesApiService();
